import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Zap, TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PRICE_REFRESH_INTERVAL = 60000; // 60 seconds

const FeaturedStockCard = ({ anomalies }) => {
  const navigate = useNavigate();
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceUpdated, setPriceUpdated] = useState(null);
  const [priceAnimation, setPriceAnimation] = useState(false);
  const refreshIntervalRef = useRef(null);
  const prevPriceRef = useRef(null);

  // Memoized selection of most interesting stock
  // Selection rule: highest Activity Score, then sigma, then volume, then abs % move
  const featuredStock = useMemo(() => {
    if (!anomalies || anomalies.length === 0) return null;

    const sorted = [...anomalies].sort((a, b) => {
      // Primary: Activity Score (highest first)
      if (b.unusual_activity_score !== a.unusual_activity_score) {
        return b.unusual_activity_score - a.unusual_activity_score;
      }
      // Tie-breaker 1: Sigma / return_zscore (higher abs value)
      const sigmaA = Math.abs(a.return_zscore || 0);
      const sigmaB = Math.abs(b.return_zscore || 0);
      if (sigmaB !== sigmaA) {
        return sigmaB - sigmaA;
      }
      // Tie-breaker 2: Volume multiple (higher)
      if (b.volume_multiple !== a.volume_multiple) {
        return b.volume_multiple - a.volume_multiple;
      }
      // Tie-breaker 3: Abs % move (larger)
      return Math.abs(b.daily_return) - Math.abs(a.daily_return);
    });

    return sorted[0];
  }, [anomalies]);

  // Fetch price for featured stock
  const fetchPrice = useCallback(async () => {
    if (!featuredStock) return;

    setPriceLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/quote?symbol=${featuredStock.symbol}`);
      const newPrice = response.data.price;

      // Trigger animation if price changed
      if (prevPriceRef.current !== null && prevPriceRef.current !== newPrice) {
        setPriceAnimation(true);
        setTimeout(() => setPriceAnimation(false), 400);
      }
      prevPriceRef.current = newPrice;

      setPrice({
        price: newPrice,
        change: response.data.change,
        changePercent: response.data.changesPercentage,
        currency: response.data.currency || 'USD'
      });
      setPriceUpdated(new Date());
    } catch (error) {
      console.warn('Failed to fetch featured stock price:', error);
      // Keep showing previous price or show placeholder
      if (!price) {
        setPrice({ price: null, currency: 'USD' });
      }
    } finally {
      setPriceLoading(false);
    }
  }, [featuredStock, price]);

  // Fetch price on mount and set up refresh interval
  useEffect(() => {
    if (featuredStock) {
      fetchPrice();

      // Set up 60-second refresh
      refreshIntervalRef.current = setInterval(fetchPrice, PRICE_REFRESH_INTERVAL);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredStock?.symbol]); // Only re-run when featured stock changes

  // Don't render if no data
  if (!featuredStock) return null;

  const isPositive = featuredStock.daily_return >= 0;
  const volumeMultiple = featuredStock.volume_multiple || 1;

  // Generate plain English explanation
  const getExplanation = () => {
    if (volumeMultiple >= 5) {
      return `Trading at ${Math.round(volumeMultiple)}× normal volume.`;
    } else if (volumeMultiple >= 2) {
      return `Trading at ${volumeMultiple.toFixed(1)}× normal volume.`;
    } else if (Math.abs(featuredStock.daily_return) >= 5) {
      return `Moved ${Math.abs(featuredStock.daily_return).toFixed(1)}% - bigger than most recent days.`;
    } else {
      return `Showing unusual activity compared to recent history.`;
    }
  };

  // Format volume badge text
  const getVolumeBadgeText = () => {
    if (volumeMultiple >= 5) {
      return `${Math.round(volumeMultiple)}× normal trading`;
    } else if (volumeMultiple >= 2) {
      return `${volumeMultiple.toFixed(1)}× normal trading`;
    } else if (volumeMultiple >= 1.5) {
      return `${volumeMultiple.toFixed(1)}× normal trading`;
    }
    return 'Above average trading';
  };

  return (
    <div
      className="mb-6 p-6 rounded-xl transition-all duration-200"
      style={{
        backgroundColor: '#0E1324',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)'
      }}
      data-testid="featured-stock-card"
    >
      {/* Header label */}
      <div className="flex items-center gap-2 mb-5">
        <Zap className="h-4 w-4" style={{ color: '#3B82F6' }} />
        <span
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: '#3B82F6' }}
        >
          Most Interesting Right Now
        </span>
      </div>

      <div className="flex items-start justify-between gap-8">
        {/* Left side content */}
        <div className="flex-1">
          {/* Ticker + Company Name */}
          <div className="flex items-baseline gap-3 mb-4">
            <span
              className="text-5xl font-bold tracking-tight"
              style={{ color: '#F8FAFC' }}
            >
              {featuredStock.symbol}
            </span>
            {featuredStock.company_name && featuredStock.company_name !== featuredStock.symbol && (
              <span className="text-lg" style={{ color: '#94A3B8' }}>
                {featuredStock.company_name}
              </span>
            )}
          </div>

          {/* Price Change + Current Price */}
          <div className="flex items-center gap-4 mb-5">
            {/* Move indicator + percentage */}
            <div className="flex items-center gap-2">
              {isPositive ? (
                <TrendingUp className="h-6 w-6" style={{ color: '#4ade80' }} />
              ) : (
                <TrendingDown className="h-6 w-6" style={{ color: '#f87171' }} />
              )}
              <span
                className="text-4xl font-bold"
                style={{ color: isPositive ? '#4ade80' : '#f87171' }}
              >
                {isPositive ? '+' : ''}{featuredStock.daily_return.toFixed(2)}%
              </span>
            </div>

            {/* Current price */}
            <span
              className={`text-3xl font-medium transition-opacity duration-300 ${priceAnimation ? 'opacity-50' : 'opacity-100'}`}
              style={{ color: '#94A3B8' }}
            >
              {price && price.price !== null ? (
                `$${price.price.toFixed(2)}`
              ) : priceLoading ? (
                <span className="text-2xl" style={{ color: '#64748B' }}>Loading...</span>
              ) : (
                '-'
              )}
            </span>
          </div>

          {/* Volume badge */}
          <div className="mb-4">
            <span
              className="px-4 py-2 rounded-full text-sm font-medium inline-block"
              style={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                color: '#60A5FA',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}
            >
              {getVolumeBadgeText()}
            </span>
          </div>

          {/* Plain English explanation */}
          <p className="text-base" style={{ color: '#CBD5E1' }}>
            {getExplanation()}
          </p>
        </div>

        {/* Right side - View Details button */}
        <div className="flex-shrink-0 flex items-start pt-8">
          <Button
            onClick={() => navigate(`/app/stock/${featuredStock.symbol}`)}
            size="lg"
            className="bg-blue-600 text-white hover:bg-blue-500 font-medium text-base px-6 py-3 h-auto"
            data-testid="featured-view-details"
          >
            View Details
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeaturedStockCard;
